import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  MapPin,
  Clock,
  MessageSquare,
  Tag,
  ChevronDown,
  Copy,
  ExternalLink,
  Linkedin,
  Plus,
  X,
  UserPlus,
  MessageCircle,
  AtSign,
  Users,
  Send,
  ArrowUpRight,
  ArrowDownLeft,
  Calendar,
  Star,
  Link2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { OutreachService } from '../services/OutreachService';
import {
  OutreachContact,
  OutreachActivity,
  OutreachStatus,
  ContactMethod,
  ActivityDirection,
  ActivityOutcome,
  OUTREACH_STATUS_LABELS,
  OUTREACH_STATUS_COLORS,
  ALL_OUTREACH_STATUSES,
  CONTACT_METHOD_LABELS,
  ACTIVITY_OUTCOME_LABELS,
  ACTIVITY_OUTCOME_COLORS,
  ICP_FIT_LABELS,
  ICP_FIT_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  getContactDisplayName,
  ICPFit,
  Priority,
} from '../types/outreach';
import { toDate } from '../types/leads';
import { useAdminAuth } from '../contexts/AdminContext';

const copyToClipboard = (text: string, label: string) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
};

function formatDate(val: Date | { toDate: () => Date } | undefined): string {
  const d = toDate(val);
  if (d.getTime() === 0) return '-';
  return d.toLocaleString('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateShort(val: Date | { toDate: () => Date } | undefined): string {
  const d = toDate(val);
  if (d.getTime() === 0) return '-';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const CONTACT_METHOD_ICON_MAP: Record<ContactMethod, React.FC<{ className?: string }>> = {
  connection_request: UserPlus,
  dm: MessageSquare,
  comment: MessageCircle,
  inmail: Mail,
  email: AtSign,
  phone: Phone,
  referral_intro: Users,
};

// ============================================
// ADD ACTIVITY MODAL
// ============================================

const AddActivityModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    contactMethod: ContactMethod;
    direction: ActivityDirection;
    summary: string;
    content?: string;
    outcome?: ActivityOutcome;
    linkedinPostUrl?: string;
  }) => void;
}> = ({ isOpen, onClose, onSave }) => {
  const [form, setForm] = useState({
    contactMethod: 'dm' as ContactMethod,
    direction: 'outbound' as ActivityDirection,
    summary: '',
    content: '',
    outcome: '' as ActivityOutcome | '',
    linkedinPostUrl: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.summary.trim()) {
      toast.error('Summary is required');
      return;
    }
    onSave({
      contactMethod: form.contactMethod,
      direction: form.direction,
      summary: form.summary,
      content: form.content || undefined,
      outcome: form.outcome || undefined,
      linkedinPostUrl: form.linkedinPostUrl || undefined,
    });
    setForm({
      contactMethod: 'dm',
      direction: 'outbound',
      summary: '',
      content: '',
      outcome: '',
      linkedinPostUrl: '',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">Add Activity</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Method</label>
              <select
                value={form.contactMethod}
                onChange={e => setForm(f => ({ ...f, contactMethod: e.target.value as ContactMethod }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                {Object.entries(CONTACT_METHOD_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Direction</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, direction: 'outbound' }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.direction === 'outbound'
                      ? 'bg-sky-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <ArrowUpRight className="w-4 h-4 inline mr-1" />
                  Outbound
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, direction: 'inbound' }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.direction === 'inbound'
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <ArrowDownLeft className="w-4 h-4 inline mr-1" />
                  Inbound
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-400 block mb-1">Summary *</label>
            <input
              type="text"
              value={form.summary}
              onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
              placeholder="e.g. Sent connection request re: trail leakage"
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            />
          </div>

          <div>
            <label className="text-sm text-slate-400 block mb-1">Full Content (optional)</label>
            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Full message text..."
              rows={3}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Outcome</label>
              <select
                value={form.outcome}
                onChange={e => setForm(f => ({ ...f, outcome: e.target.value as ActivityOutcome | '' }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="">Not set</option>
                {Object.entries(ACTIVITY_OUTCOME_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">LinkedIn Post URL</label>
              <input
                type="text"
                value={form.linkedinPostUrl}
                onChange={e => setForm(f => ({ ...f, linkedinPostUrl: e.target.value }))}
                placeholder="Optional"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Activity
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// DETAIL PAGE
// ============================================

const OutreachDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAdminAuth();

  const [contact, setContact] = useState<OutreachContact | null>(null);
  const [activities, setActivities] = useState<OutreachActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Status management
  const [newStatus, setNewStatus] = useState<OutreachStatus | ''>('');
  const [statusNote, setStatusNote] = useState('');

  // Notes
  const [noteText, setNoteText] = useState('');

  // Activity modal
  const [showActivityModal, setShowActivityModal] = useState(false);

  // Follow-up date
  const [followUpDate, setFollowUpDate] = useState('');

  // Tags
  const [tagInput, setTagInput] = useState('');

  // Editing contact fields
  const [showEditFields, setShowEditFields] = useState(false);
  const [editForm, setEditForm] = useState<Partial<OutreachContact>>({});

  const docId = id || '';

  // ---- Data Loading ----

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [contactData, activityData] = await Promise.all([
          OutreachService.getContact(docId),
          OutreachService.getActivities(docId),
        ]);
        setContact(contactData);
        setActivities(activityData);
        if (contactData) {
          setNewStatus(contactData.status);
          if (contactData.nextFollowUpDate) {
            const d = toDate(contactData.nextFollowUpDate);
            if (d.getTime() > 0) {
              setFollowUpDate(d.toISOString().split('T')[0]);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching contact:', err);
        toast.error('Failed to load contact');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, docId]);

  const refetch = async () => {
    const [contactData, activityData] = await Promise.all([
      OutreachService.getContact(docId),
      OutreachService.getActivities(docId),
    ]);
    if (contactData) {
      setContact(contactData);
      setNewStatus(contactData.status);
    }
    setActivities(activityData);
  };

  // ---- Handlers ----

  const handleUpdateStatus = async () => {
    if (!contact || !newStatus || newStatus === contact.status) return;
    setUpdating(true);
    try {
      await OutreachService.updateContactStatus(
        docId,
        newStatus as OutreachStatus,
        user?.email || 'admin',
        statusNote || undefined
      );
      toast.success(`Status updated to ${OUTREACH_STATUS_LABELS[newStatus as OutreachStatus]}`);
      setStatusNote('');
      await refetch();
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setUpdating(true);
    try {
      await OutreachService.addNote(docId, noteText.trim(), user?.email || 'admin');
      toast.success('Note added');
      setNoteText('');
      await refetch();
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddActivity = async (data: {
    contactMethod: ContactMethod;
    direction: ActivityDirection;
    summary: string;
    content?: string;
    outcome?: ActivityOutcome;
    linkedinPostUrl?: string;
  }) => {
    setUpdating(true);
    try {
      await OutreachService.addActivity(docId, data, user?.email || 'admin');
      toast.success('Activity logged');
      setShowActivityModal(false);
      await refetch();
    } catch (err) {
      console.error('Error adding activity:', err);
      toast.error('Failed to log activity');
    } finally {
      setUpdating(false);
    }
  };

  const handleSetFollowUp = async () => {
    setUpdating(true);
    try {
      const date = followUpDate ? new Date(followUpDate) : null;
      await OutreachService.setFollowUpDate(docId, date);
      toast.success(date ? `Follow-up set for ${formatDateShort(date)}` : 'Follow-up cleared');
      await refetch();
    } catch (err) {
      console.error('Error setting follow-up:', err);
      toast.error('Failed to set follow-up');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddTag = async () => {
    if (!tagInput.trim() || !contact) return;
    const newTags = [...(contact.tags || []), tagInput.trim().toLowerCase().replace(/\s+/g, '_')];
    try {
      await OutreachService.updateTags(docId, newTags);
      setTagInput('');
      await refetch();
      toast.success('Tag added');
    } catch (err) {
      toast.error('Failed to add tag');
    }
  };

  const handleRemoveTag = async (tag: string) => {
    if (!contact) return;
    const newTags = (contact.tags || []).filter(t => t !== tag);
    try {
      await OutreachService.updateTags(docId, newTags);
      await refetch();
      toast.success('Tag removed');
    } catch (err) {
      toast.error('Failed to remove tag');
    }
  };

  const handleSaveEdit = async () => {
    if (!contact) return;
    setUpdating(true);
    try {
      await OutreachService.updateContact(docId, editForm);
      toast.success('Contact updated');
      setShowEditFields(false);
      await refetch();
    } catch (err) {
      console.error('Error updating contact:', err);
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  // ---- Loading / Error States ----

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Loading contact...</p>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <X className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-white mb-2">Contact Not Found</h2>
          <p className="text-slate-400 mb-4">This contact does not exist.</p>
          <button
            onClick={() => navigate('/outreach')}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-lg px-4 py-2 transition-colors"
          >
            Back to Outreach
          </button>
        </div>
      </div>
    );
  }

  const displayName = getContactDisplayName(contact);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">

        {/* Back Button */}
        <button
          onClick={() => navigate('/outreach')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm">Back to Outreach</span>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-sky-600 to-blue-700 rounded-xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white">{displayName}</h1>
              {contact.company && (
                <p className="text-sky-100 mt-1">{contact.company}</p>
              )}
              {contact.role && (
                <p className="text-sky-200 text-sm mt-1">{contact.role}</p>
              )}
              <p className="text-sky-200 text-sm mt-2">
                Added {formatDate(contact.createdAt)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Status badge */}
              <span
                className="inline-flex items-center text-xs font-medium px-3 py-1 rounded-full"
                style={{
                  backgroundColor: `${OUTREACH_STATUS_COLORS[contact.status]}33`,
                  color: OUTREACH_STATUS_COLORS[contact.status],
                }}
              >
                {OUTREACH_STATUS_LABELS[contact.status]}
              </span>
              {/* Priority badge */}
              {contact.priority && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full"
                  style={{
                    backgroundColor: `${PRIORITY_COLORS[contact.priority]}33`,
                    color: PRIORITY_COLORS[contact.priority],
                  }}
                >
                  <Star className="w-3 h-3" />
                  {PRIORITY_LABELS[contact.priority]}
                </span>
              )}
              {/* ICP fit badge */}
              {contact.icpFit && (
                <span
                  className="inline-flex items-center text-xs font-medium px-3 py-1 rounded-full"
                  style={{
                    backgroundColor: `${ICP_FIT_COLORS[contact.icpFit]}33`,
                    color: ICP_FIT_COLORS[contact.icpFit],
                  }}
                >
                  {ICP_FIT_LABELS[contact.icpFit]}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Contact Info Card */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Linkedin className="w-5 h-5 text-sky-400" />
                Contact Information
              </h2>
              <button
                onClick={() => {
                  setShowEditFields(!showEditFields);
                  setEditForm({
                    email: contact.email,
                    phone: contact.phone,
                    location: contact.location,
                    role: contact.role,
                    linkedinUrl: contact.linkedinUrl,
                    website: contact.website,
                    birthday: contact.birthday,
                  });
                }}
                className="text-xs text-slate-400 hover:text-sky-400 transition-colors"
              >
                {showEditFields ? 'Cancel' : 'Edit'}
              </button>
            </div>

            {showEditFields ? (
              <div className="space-y-3">
                {(['linkedinUrl', 'email', 'phone', 'location', 'website', 'birthday', 'role'] as const).map(field => (
                  <div key={field}>
                    <label className="text-xs text-slate-400 block mb-1 capitalize">{field === 'linkedinUrl' ? 'LinkedIn URL' : field}</label>
                    <input
                      type="text"
                      value={(editForm as any)[field] || ''}
                      onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                ))}
                <button
                  onClick={handleSaveEdit}
                  disabled={updating}
                  className="bg-sky-500 hover:bg-sky-600 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {updating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            ) : (
              <>
                {/* LinkedIn URL */}
                {contact.linkedinUrl && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">LinkedIn</p>
                    <div className="flex items-center gap-2">
                      <a
                        href={contact.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-400 hover:text-sky-300 transition-colors flex items-center gap-1 text-sm break-all"
                      >
                        <Linkedin className="w-3.5 h-3.5 flex-shrink-0" />
                        Open in LinkedIn
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                      <button
                        onClick={() => copyToClipboard(contact.linkedinUrl!, 'LinkedIn URL')}
                        className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Email */}
                {contact.email && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">Email</p>
                    <div className="flex items-center gap-2">
                      <a href={`mailto:${contact.email}`} className="text-sky-400 hover:text-sky-300 transition-colors break-all text-sm">
                        {contact.email}
                      </a>
                      <button
                        onClick={() => copyToClipboard(contact.email!, 'Email')}
                        className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Phone */}
                {contact.phone && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">Phone</p>
                    <div className="flex items-center gap-2">
                      <a href={`tel:${contact.phone}`} className="text-sky-400 hover:text-sky-300 transition-colors flex items-center gap-1 text-sm">
                        <Phone className="w-3.5 h-3.5" />
                        {contact.phone}
                      </a>
                      <button
                        onClick={() => copyToClipboard(contact.phone!, 'Phone')}
                        className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Location */}
                {contact.location && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">Location</p>
                    <p className="text-slate-200 flex items-center gap-1 text-sm">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      {contact.location}
                    </p>
                  </div>
                )}

                {/* Website */}
                {contact.website && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">Website</p>
                    <a
                      href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-400 hover:text-sky-300 transition-colors flex items-center gap-1 text-sm break-all"
                    >
                      {contact.website}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                )}

                {/* Birthday */}
                {contact.birthday && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">Birthday</p>
                    <p className="text-slate-200 flex items-center gap-1 text-sm">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {contact.birthday}
                    </p>
                  </div>
                )}

                {/* Found Via */}
                {contact.foundVia && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">Found Via</p>
                    <p className="text-slate-200 text-sm">{contact.foundVia}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Qualification Card */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-sky-400" />
              Qualification
            </h2>

            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Company', value: contact.company },
                { label: 'Role', value: contact.role },
                { label: 'Est. Brokers', value: contact.estimatedBrokerCount },
                { label: 'Offices', value: contact.numberOfOffices },
                { label: 'Aggregator', value: contact.aggregator },
                { label: 'ICP Fit', value: contact.icpFit ? ICP_FIT_LABELS[contact.icpFit] : undefined },
              ]
                .filter(f => f.value)
                .map(field => (
                  <div key={field.label} className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                    <p className="text-xs text-slate-500 mb-1">{field.label}</p>
                    <p className="text-sm text-slate-200">{field.value}</p>
                  </div>
                ))}
            </div>

            {/* Linked Lead */}
            {contact.linkedLeadRef && (
              <div className="border-t border-slate-700 pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="w-4 h-4 text-amber-400" />
                  <p className="text-sm font-medium text-white">Linked Inbound Lead</p>
                </div>
                <button
                  onClick={() => navigate(`/leads/${contact.linkedLeadType}/${contact.linkedLeadId}`)}
                  className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1"
                >
                  View lead ({contact.linkedLeadType}/{contact.linkedLeadId})
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Status Management */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-5">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-sky-400" />
            Status Management
          </h2>

          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">Current Status:</span>
            <span
              className="inline-flex items-center text-sm font-semibold px-3 py-1 rounded-full"
              style={{
                backgroundColor: `${OUTREACH_STATUS_COLORS[contact.status]}33`,
                color: OUTREACH_STATUS_COLORS[contact.status],
              }}
            >
              {OUTREACH_STATUS_LABELS[contact.status]}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Change Status</label>
              <select
                value={newStatus}
                onChange={e => setNewStatus(e.target.value as OutreachStatus)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="">Select status...</option>
                {ALL_OUTREACH_STATUSES.map(s => (
                  <option key={s} value={s}>{OUTREACH_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Note (optional)</label>
              <textarea
                value={statusNote}
                onChange={e => setStatusNote(e.target.value)}
                placeholder="Reason for status change..."
                rows={1}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
              />
            </div>
          </div>
          <button
            onClick={handleUpdateStatus}
            disabled={updating || !newStatus || newStatus === contact.status}
            className="bg-sky-500 hover:bg-sky-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
          >
            {updating ? 'Updating...' : 'Update Status'}
          </button>

          {/* Follow-up Date */}
          <div className="border-t border-slate-700 pt-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-sky-400" />
              <label className="text-sm text-slate-400">Next Follow-up</label>
              <input
                type="date"
                value={followUpDate}
                onChange={e => setFollowUpDate(e.target.value)}
                className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
              <button
                onClick={handleSetFollowUp}
                disabled={updating}
                className="bg-sky-500 hover:bg-sky-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
              >
                Set
              </button>
              {followUpDate && (
                <button
                  onClick={() => { setFollowUpDate(''); handleSetFollowUp(); }}
                  className="text-xs text-slate-400 hover:text-red-400"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Status Timeline */}
        {contact.statusHistory && contact.statusHistory.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-sky-400" />
              Status Timeline
            </h2>
            <div className="relative ml-3">
              <div className="absolute left-[5px] top-2 bottom-2 w-0.5 bg-slate-700" />
              <div className="space-y-4">
                {[...contact.statusHistory]
                  .sort((a, b) => toDate(b.changedAt).getTime() - toDate(a.changedAt).getTime())
                  .map((entry, idx) => {
                    const toStatus = entry.to as OutreachStatus;
                    const color = OUTREACH_STATUS_COLORS[toStatus] || '#6b7280';
                    return (
                      <div key={idx} className="relative pl-6">
                        <div
                          className="absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 border-slate-800"
                          style={{ backgroundColor: color }}
                        />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className="text-sm font-medium px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: `${color}33`, color }}
                            >
                              {OUTREACH_STATUS_LABELS[toStatus] || entry.to}
                            </span>
                            {entry.from && (
                              <span className="text-xs text-slate-500">
                                from {OUTREACH_STATUS_LABELS[entry.from as OutreachStatus] || entry.from}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            <span>by {entry.changedBy}</span>
                            <span>-</span>
                            <span>{formatDate(entry.changedAt)}</span>
                          </div>
                          {entry.note && (
                            <p className="text-sm text-slate-400 mt-1 italic">&ldquo;{entry.note}&rdquo;</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* Activity Timeline */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-sky-400" />
              Activity Timeline ({activities.length})
            </h2>
            <button
              onClick={() => setShowActivityModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Activity
            </button>
          </div>

          {activities.length > 0 ? (
            <div className="relative ml-3">
              <div className="absolute left-[5px] top-2 bottom-2 w-0.5 bg-slate-700" />
              <div className="space-y-4">
                {activities.map((activity) => {
                  const IconComponent = CONTACT_METHOD_ICON_MAP[activity.contactMethod] || MessageSquare;
                  const outcomeColor = activity.outcome ? ACTIVITY_OUTCOME_COLORS[activity.outcome] : undefined;
                  const isOutbound = activity.direction === 'outbound';

                  return (
                    <div key={activity.id} className="relative pl-6">
                      <div className={`absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 border-slate-800 ${
                        isOutbound ? 'bg-sky-500' : 'bg-green-500'
                      }`} />
                      <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <IconComponent className="w-4 h-4 text-slate-400" />
                          <span className="text-xs font-medium text-slate-300">
                            {CONTACT_METHOD_LABELS[activity.contactMethod]}
                          </span>
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                            isOutbound ? 'bg-sky-500/20 text-sky-300' : 'bg-green-500/20 text-green-300'
                          }`}>
                            {isOutbound ? (
                              <><ArrowUpRight className="w-3 h-3" /> Outbound</>
                            ) : (
                              <><ArrowDownLeft className="w-3 h-3" /> Inbound</>
                            )}
                          </span>
                          {activity.outcome && (
                            <span
                              className="text-xs px-2 py-0.5 rounded"
                              style={{ backgroundColor: `${outcomeColor}20`, color: outcomeColor }}
                            >
                              {ACTIVITY_OUTCOME_LABELS[activity.outcome]}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white">{activity.summary}</p>
                        {activity.content && (
                          <p className="text-xs text-slate-400 mt-2 bg-slate-800 rounded p-2 whitespace-pre-wrap">
                            {activity.content}
                          </p>
                        )}
                        {activity.response && (
                          <div className="mt-2 bg-slate-800 rounded p-2">
                            <p className="text-xs text-slate-500 mb-1">Their response:</p>
                            <p className="text-xs text-slate-300 whitespace-pre-wrap">{activity.response}</p>
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-2">
                          {formatDate(activity.timestamp)} — {activity.createdBy}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-slate-400 text-sm italic">No activities logged yet. Click "Add Activity" to start tracking interactions.</p>
          )}
        </div>

        {/* Tags */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-sky-400" />
            Tags
          </h2>

          <div className="flex flex-wrap gap-2">
            {(contact.tags || []).map(tag => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 bg-sky-500/20 text-sky-300 text-xs font-medium px-3 py-1 rounded-full"
              >
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="hover:text-red-400 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {(!contact.tags || contact.tags.length === 0) && (
              <span className="text-slate-500 text-sm italic">No tags</span>
            )}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
              placeholder="Add tag..."
              className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
            <button
              onClick={handleAddTag}
              disabled={!tagInput.trim()}
              className="bg-sky-500 hover:bg-sky-600 disabled:bg-slate-600 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-sky-400" />
            Notes
          </h2>

          {contact.notes ? (
            <pre className="text-sm text-slate-300 whitespace-pre-wrap bg-slate-900 rounded-lg p-4 border border-slate-700 font-sans leading-relaxed">
              {contact.notes}
            </pre>
          ) : (
            <p className="text-slate-500 text-sm italic">No notes yet</p>
          )}

          <div className="space-y-2">
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Add a note..."
              rows={3}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
            />
            <button
              onClick={handleAddNote}
              disabled={updating || !noteText.trim()}
              className="bg-sky-500 hover:bg-sky-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            >
              {updating ? 'Adding...' : 'Add Note'}
            </button>
          </div>
        </div>

      </div>

      {/* Add Activity Modal */}
      <AddActivityModal
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        onSave={handleAddActivity}
      />
    </div>
  );
};

export default OutreachDetailPage;
